import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { useSession } from "../../stores/session";

export type ConsoleTown = {
  id: string;
  name: string;
  slug: string;
  status: string;
  timeScale: number;
  tokenBalance: number;
};

type ConsoleContextValue = {
  town: ConsoleTown;
  towns: ConsoleTown[];
  selectTown: (townId: string) => void;
  reloadTowns: () => Promise<ConsoleTown[]>;
};

const ConsoleTownContext = createContext<ConsoleContextValue | null>(null);

export function useConsoleTown() {
  const value = useContext(ConsoleTownContext);
  if (!value) throw new Error("useConsoleTown must be used inside ConsoleLayout");
  return value;
}

const NAV = [
  ["overview", "概览"],
  ["residents", "居民"],
  ["settings", "设置"],
  ["wallet", "钱包"],
  ["moderation", "内容巡查"],
] as const;

const STATUS_LABEL: Record<string, string> = {
  active: "● 活跃",
  slow: "🐌 慢速",
  sleeping: "💤 沉睡",
  closed: "已关闭",
};

export default function ConsoleLayout() {
  const { user, logout } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [towns, setTowns] = useState<ConsoleTown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reloadTowns = async () => {
    const result = await api.consoleTowns();
    const next = (result.towns || []) as ConsoleTown[];
    setTowns(next);
    return next;
  };

  useEffect(() => {
    reloadTowns()
      .catch((reason) => setError(reason.message || "小镇列表加载失败"))
      .finally(() => setLoading(false));
  }, []);

  const selectedTownId = searchParams.get("town") || "";
  const town = useMemo(() => towns.find((item) => item.id === selectedTownId) || towns[0], [towns, selectedTownId]);

  useEffect(() => {
    if (town && town.id !== selectedTownId) {
      const next = new URLSearchParams(searchParams);
      next.set("town", town.id);
      setSearchParams(next, { replace: true });
    }
  }, [town, selectedTownId, searchParams, setSearchParams]);

  const selectTown = (townId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("town", townId);
    setSearchParams(next);
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#f3f0e6] text-[#665f4f]">正在加载城主控制台...</div>;
  if (error) return <div className="min-h-screen grid place-items-center bg-[#f3f0e6] p-6 text-red-700">{error}</div>;
  if (!town) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#f3f0e6] p-6">
        <div className="rounded-xl border border-[#ddd7c4] bg-white p-8 text-center">
          <div className="font-bold">当前账号名下还没有可管理的小镇</div>
          <button onClick={() => navigate("/halls")} className="mt-4 rounded-lg bg-[#2f6e5b] px-4 py-2 text-sm font-bold text-white">前往小镇大厅</button>
        </div>
      </div>
    );
  }

  const value = { town, towns, selectTown, reloadTowns };
  return (
    <ConsoleTownContext.Provider value={value}>
      <div className="min-h-screen bg-[#f3f0e6] text-[#28251f]">
        <header className="border-b border-[#ddd7c4] bg-[#18352f] text-white">
          <div className="mx-auto flex max-w-7xl items-center gap-4 px-5 py-3">
            <span className="text-lg font-black">星原小镇 · 城主控制台</span>
            <div className="flex-1" />
            <span className="hidden text-sm text-[#c8d8d2] sm:inline">{user?.username}</span>
            <button onClick={logout} className="rounded-md border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10">退出</button>
          </div>
        </header>

        <div className="mx-auto grid max-w-7xl gap-5 px-5 py-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="space-y-3">
            <div className="rounded-xl border border-[#ddd7c4] bg-white p-3 shadow-sm">
              <div className="mb-2 text-xs font-bold text-[#77705f]">当前小镇</div>
              <select
                value={town.id}
                onChange={(event) => selectTown(event.target.value)}
                className="w-full rounded-lg border border-[#d8d2bf] bg-white px-3 py-2 text-sm outline-none focus:border-[#2f6e5b]"
              >
                {towns.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <div className="mt-3 flex items-center justify-between text-xs text-[#665f4f]">
                <span>{STATUS_LABEL[town.status] || town.status}</span>
                <span>{town.timeScale}x</span>
              </div>
              <div className="mt-2 text-sm font-bold text-[#2f6e5b]">余额 {Number(town.tokenBalance || 0).toLocaleString()}</div>
            </div>
            <nav className="rounded-xl border border-[#ddd7c4] bg-white p-2 shadow-sm">
              {NAV.map(([path, label]) => (
                <NavLink
                  key={path}
                  to={`/console/${path}?town=${encodeURIComponent(town.id)}`}
                  className={({ isActive }) => `block rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-[#e0eee8] text-[#245849]" : "text-[#4d493f] hover:bg-[#f3f0e6]"}`}
                >
                  {label}
                </NavLink>
              ))}
            </nav>
            <button onClick={() => navigate(`/town/${town.id}`)} className="w-full rounded-lg border border-[#cfc8b5] bg-white px-3 py-2 text-sm font-medium text-[#4d493f]">查看镇内页面</button>
            <button onClick={() => navigate("/halls")} className="w-full rounded-lg border border-[#cfc8b5] bg-white px-3 py-2 text-sm font-medium text-[#4d493f]">返回小镇大厅</button>
          </aside>
          <main className="min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </ConsoleTownContext.Provider>
  );
}
